import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { combine, subscribeWithSelector } from 'zustand/middleware';
import { eventBus } from '../event-manager/event-bus';
import JitsiMeetJS from '../lib-jitsi-meet/lib';
import { type ValueOf } from '../types';
import {
  CONFERENCE_COMMANDS,
  EVENT_LISTENER_METHODS,
  INTERNAL_EVENTS,
  tryCatch,
} from '../utils';
import { sendParticipantEvent } from '../utils/send-participant-event';
import { useConfigStore } from './config';
import { useParticipantStore } from './participants';

type ConferenceState = {
  conferenceError?: string;
  conference: any;
  conferenceJoined: boolean;
  conferenceJoinedAt: number | null;
  conferenceStartedAt: number | null;
  p2p: boolean;
  conferenceStatus:
    | 'initial'
    | 'joining'
    | 'joined'
    | 'error'
    | 'interrupted'
    | 'left'
    | 'restored'
    | 'reconnecting';
  connectionQuality: Map<string, number>;
  raiseHandMap: Map<string, number>;
  recording: {
    status: ValueOf<typeof JitsiMeetJS.constants.recording.status>;
    mode: ValueOf<typeof JitsiMeetJS.constants.recording.mode>;
    error?: string;
    sessionId?: string;
    initiator?: any;
  };
  isRecording: boolean;
};

type Actions = {
  reset: () => void;
  conferenceWillJoin: (conference: any) => void;
  raiseHand: (participantId: string, timestamp: number) => void;
  lowerHand: (participantId: string) => void;
  leaveConference: () => Promise<void>;
  endConference: () => Promise<void>;
  stopRecording: () => Promise<void>;
};

const initialState: ConferenceState = {
  conference: null,
  conferenceJoined: false,
  conferenceJoinedAt: null,
  conferenceStartedAt: null,
  conferenceStatus: 'initial',
  p2p: false,
  connectionQuality: new Map(),
  raiseHandMap: new Map(),
  recording: {
    status: JitsiMeetJS.constants.recording.status.OFF,
    mode: JitsiMeetJS.constants.recording.mode.FILE,
  },
  isRecording: false,
};

export const useConferenceStore = create<ConferenceState & Actions>()(
  subscribeWithSelector(
    combine(initialState, set => ({
      conferenceWillJoin: (conference: any) => {
        if (typeof window?.APP !== 'undefined') {
          window.APP.conference._room = conference;
        }
        set({ conference, conferenceStatus: 'joining' });
      },
      reset: () => {
        set(initialState);
      },
      raiseHand: (participantId: string, timestamp: number) => {
        set(state => ({
          raiseHandMap: new Map(state.raiseHandMap).set(
            participantId,
            timestamp,
          ),
        }));
        sendParticipantEvent(
          EVENT_LISTENER_METHODS.ParticipantEventsListner
            .onParticipantHandRaised,
          participantId,
        );
      },
      lowerHand: (participantId: string) => {
        const hasRaisedHand = useConferenceStore
          .getState()
          .raiseHandMap.has(participantId);
        if (!hasRaisedHand) return;

        set(state => {
          const raiseHandMap = new Map(state.raiseHandMap);
          raiseHandMap.delete(participantId);
          return { raiseHandMap };
        });
        sendParticipantEvent(
          EVENT_LISTENER_METHODS.ParticipantEventsListner
            .onParticipantHandLowered,
          participantId,
        );
      },
      leaveConference: async () => {
        const conference = useConferenceStore.getState().conference;
        if (conference) {
          const { error } = await tryCatch(conference.leave(), 500);
          if (error) {
            console.warn('Error leaving conference:', error);
            eventBus.publish({
              type: EVENT_LISTENER_METHODS.SessionStatusListener.onSessionLeft,
            });
          }
        }
      },
      endConference: async () => {
        const conference = useConferenceStore.getState().conference;
        if (conference) {
          const { error } = await tryCatch(conference.end());
          if (error) {
            console.warn('Error ending conference:', error);
          }
        }
      },
      stopRecording: async () => {
        const conference = useConferenceStore.getState().conference;
        if (conference) {
          const recording = useConferenceStore.getState().recording;
          if (recording.sessionId) {
            const { error } = await tryCatch(
              conference.stopRecording(recording.sessionId),
            );
            if (error) {
              console.warn('Error stopping recording:', error);
            }
          }
        }
      },
    })),
  ),
);

export const useSessionTimer = () => {
  const [conferenceElapsedTime, setConferenceElapsedTime] = useState(0);
  const conferenceStartedAt = useConferenceStore(
    state => state.conferenceStartedAt,
  );

  useEffect(() => {
    const interval = setInterval(() => {
      if (!conferenceStartedAt) {
        setConferenceElapsedTime(0); // Reset if not started
        return;
      }
      setConferenceElapsedTime(Math.max(Date.now() - conferenceStartedAt, 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [conferenceStartedAt]);

  return conferenceElapsedTime;
};

export function raisedHandLocal() {
  const conference = useConferenceStore.getState().conference;
  if (conference) {
    const currentTime = Date.now();
    const localParticipantId =
      useParticipantStore.getState().localParticipant.pid;
    conference?.setLocalParticipantProperty('raisedHand', currentTime);
    useConferenceStore.getState().raiseHand(localParticipantId, currentTime);
  }
}

export function lowerHandLocal() {
  const conference = useConferenceStore.getState().conference;
  if (conference) {
    const localParticipantId =
      useParticipantStore.getState().localParticipant.pid;
    conference.setLocalParticipantProperty('raisedHand', 0);

    useConferenceStore.getState().lowerHand(localParticipantId);
  }
}

export function toggleRaiseHand() {
  const conference = useConferenceStore.getState().conference;
  const raisedHand = conference?.getLocalParticipantProperty('raisedHand');
  if (raisedHand) {
    lowerHandLocal();
  } else {
    raisedHandLocal();
  }
}
export const useIsRecording = () => {
  const isRecording = useConferenceStore(state => state.isRecording);
  const iAmRecorder = useConfigStore(state => state.iAmRecorder);
  return !iAmRecorder && isRecording;
};

useConferenceStore.subscribe(
  state => state.recording,
  recording => {
    const recordingStatus = recording.status;
    const recordingMode = recording.mode;
    const recordingError = recording.error;
    const initiator = recording.initiator;
    const isRecording =
      !recordingError &&
      initiator &&
      recordingMode === JitsiMeetJS.constants.recording.mode.FILE &&
      (recordingStatus === JitsiMeetJS.constants.recording.status.ON ||
        recordingStatus === JitsiMeetJS.constants.recording.status.PENDING);
    if (isRecording) {
      useConferenceStore.setState({ isRecording: true });
    } else {
      useConferenceStore.setState({ isRecording: false });
    }
  },
);

useConferenceStore.subscribe(
  state => state.isRecording,
  isRecording => {
    const initiator = useConferenceStore
      .getState()
      .recording.initiator?.getId?.();
    const localParticipantId =
      useParticipantStore.getState().localParticipant.pid;
    if (isRecording) {
      if (initiator && initiator !== localParticipantId) {
        sendParticipantEvent(
          EVENT_LISTENER_METHODS.ParticipantEventsListner
            .onParticipantStartedRecording,
          initiator,
        );
      } else {
        eventBus.publish({
          type: EVENT_LISTENER_METHODS.MediaEventsListener.onRecordingStarted,
        });
      }
      eventBus.publish({
        type: INTERNAL_EVENTS.notification,
        payload: {
          type: 'info',
          // TODO: Localize this message
          message: 'The session is being recorded',
        },
      });
    } else {
      if (initiator && initiator !== localParticipantId) {
        sendParticipantEvent(
          EVENT_LISTENER_METHODS.ParticipantEventsListner
            .onParticipantStoppedRecording,
          initiator,
        );
      } else {
        eventBus.publish({
          type: EVENT_LISTENER_METHODS.MediaEventsListener.onRecordingStopped,
        });
      }
      eventBus.publish({
        type: INTERNAL_EVENTS.notification,
        payload: {
          type: 'info',
          // TODO: Localize this message
          message: 'Recording has been stopped',
        },
      });
    }
  },
);

export async function startRecording() {
  const conference = useConferenceStore.getState().conference;
  if (conference) {
    const { data, error } = await tryCatch(
      conference.startRecording({
        mode: JitsiMeetJS.constants.recording.mode.FILE,
        appData: JSON.stringify({
          startedAt: Date.now(),
        }),
      }),
    );
    if (data) {
      console.log('Recording started successfully:', data);
    }
    if (error) {
      // TODO: Show a notification or alert to the user
    }
  }
}

export async function stopRecording() {
  useConferenceStore.getState().stopRecording();
}

export async function toggleRecording() {
  const isRecording = useConferenceStore.getState().isRecording;
  if (isRecording) {
    await stopRecording();
  } else {
    await startRecording();
  }
}

export const getIsConferenceJoined = () => {
  const conferenceStatus = useConferenceStore.getState().conferenceStatus;
  return conferenceStatus === 'joined' || conferenceStatus === 'restored';
};

useConferenceStore.subscribe(
  state => state.conference,
  conference => {
    if (conference) {
      const localParticipant = useParticipantStore.getState().localParticipant;

      conference?.sendCommand(CONFERENCE_COMMANDS.userInfo, {
        value: JSON.stringify(localParticipant),
      });
    }
  },
);
